import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";

// Shared bulk-delete control rendered inside a list's bulk-actions
// toolbar. Fires the per-row deleteFn in parallel batches (5 at a time)
// so a 50-row purge doesn't thrash the API, and reports any failures
// inline. Confirmation modal is the only modal we open; the parent's
// selection state is reset on full success.
export default function BulkDeleteAction({ ids, deleteFn, label, reset, reload }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true); setErr("");
    const failures = [];
    const queue = [...ids];
    const workers = Array(Math.min(5, queue.length)).fill(null).map(async () => {
      while (queue.length) {
        const id = queue.shift();
        try { await deleteFn(id); }
        catch (e) { failures.push({ id, msg: e?.response?.data?.detail || e?.message || "delete failed" }); }
      }
    });
    await Promise.all(workers);
    setBusy(false);
    setConfirmOpen(false);
    if (failures.length) {
      setErr(`${failures.length} of ${ids.length} delete${ids.length === 1 ? "" : "s"} failed`);
    } else {
      reset?.();
    }
    reload?.();
  };

  return (
    <>
      <button
        onClick={() => setConfirmOpen(true)}
        disabled={busy}
        className="text-[11px] uppercase tracking-wider px-3 py-1 rounded bg-rose-500/15 border border-rose-500/40 text-rose-200 hover:bg-rose-500/25 inline-flex items-center gap-1 disabled:opacity-50"
      >
        <Trash2 className="w-3 h-3" />
        Delete {ids.length}
      </button>
      {err && <span className="text-[10px] text-rose-300 ml-2">{err}</span>}
      {confirmOpen && (
        <BulkDeleteConfirm
          ids={ids}
          label={label}
          busy={busy}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={run}
        />
      )}
      {/* old inline confirm replaced by BulkDeleteConfirm below — left
          here to anchor the original render position. */}
      {false && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-bg-primary border border-border-default rounded-xl shadow-2xl max-w-sm w-full p-5">
            <h3 className="text-base font-semibold mb-2">Delete {ids.length} {label}?</h3>
            <p className="text-sm text-text-secondary mb-4">
              This cannot be undone. Anything that references one of these {label} will be unlinked.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} disabled={busy} className="px-3 py-1.5 rounded bg-bg-secondary text-text-secondary text-sm">Cancel</button>
              <button onClick={run} disabled={busy} className="px-3 py-1.5 rounded bg-rose-600 text-white text-sm">
                {busy ? "Deleting…" : `Delete ${ids.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// 2026-06-08 — Esc / backdrop dismiss for the bulk-delete confirm.
// Inline-defined so we don't drag useDialogDismiss into this small
// component; one-shot Esc handler covers it.
function BulkDeleteConfirm({ ids, label, busy, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Delete ${ids.length} ${label}`}
        className="bg-bg-primary border border-border-default rounded-xl shadow-2xl max-w-sm w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold mb-2">Delete {ids.length} {label}?</h3>
        <p className="text-sm text-text-secondary mb-4">
          This cannot be undone. Anything that references one of these {label} will be unlinked.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="px-3 py-1.5 rounded bg-bg-secondary text-text-secondary text-sm">Cancel</button>
          <button onClick={onConfirm} disabled={busy} className="px-3 py-1.5 rounded bg-rose-600 text-white text-sm">
            {busy ? "Deleting…" : `Delete ${ids.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
